// ABC SEO/GEO — left sidebar. Dark navy gradient, grouped nav, PRO usage card.
const { Icon } = window.SGIcons;

const SG_NAV = [
  { group: 'Overview', items: [
    { id: 'dashboard', label: 'Dashboard', icon: 'layout-dashboard' },
    { id: 'geo', label: 'GEO / AI Visibility', icon: 'sparkles', ai: true },
    { id: 'agent', label: 'Agent Readiness', icon: 'globe', ai: true },
  ] },
  { group: 'Research', items: [
    { id: 'search', label: 'Search Performance', icon: 'trending-up', live: true },
    { id: 'audit', label: 'Site Audit', icon: 'activity' },
    { id: 'keywords', label: 'Keywords', icon: 'key' },
    { id: 'rank', label: 'Rank Tracking', icon: 'list-ordered' },
    { id: 'competitors', label: 'Competitors', icon: 'target' },
    { id: 'backlinks', label: 'Backlinks', icon: 'link' },
    { id: 'video', label: 'Video SEO', icon: 'play', ai: true },
    { id: 'listings', label: 'Local Listings', icon: 'map-pin' },
  ] },
  { group: 'Create', items: [
    { id: 'content', label: 'Content Studio', icon: 'edit', ai: true },
  ] },
  { group: 'Deliver', items: [
    { id: 'reports', label: 'Reports', icon: 'file-text' },
    { id: 'settings', label: 'Settings', icon: 'settings' },
  ] },
];

function SGNavItem({ item, active, onClick }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '0 12px', height: 40,
        border: 'none', borderRadius: 'var(--sg-radius-md)', cursor: 'pointer', textAlign: 'left',
        fontFamily: 'var(--sg-font-sans)', fontSize: 14, fontWeight: active ? 600 : 500,
        background: active ? 'rgba(255,255,255,0.13)' : hover ? 'rgba(255,255,255,0.06)' : 'transparent',
        color: active ? '#FFFFFF' : 'rgba(226,232,240,0.78)',
        transition: 'background 140ms var(--sg-ease), color 140ms var(--sg-ease)',
        position: 'relative',
      }}>
      <Icon name={item.icon} size={18} strokeWidth={active ? 2.3 : 2}
        color={item.ai ? 'var(--sg-violet-500)' : 'inherit'} />
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.ai && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: '#C9B6F7',
            background: 'rgba(139,92,246,0.22)', padding: '2px 6px', borderRadius: 'var(--sg-radius-pill)' }}>AI</span>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--sg-violet-500)',
            boxShadow: '0 0 8px rgba(139,92,246,0.9)' }} />
        </span>
      )}
      {item.live && (
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#34D399',
          boxShadow: '0 0 8px rgba(52,211,153,0.9)' }} />
      )}
    </button>
  );
}

// Next-tier GHL payment links — the "Upgrade plan" CTA target per current tier.
const SG_UPGRADE_LINK = {
  starter: 'https://links.aibizconnect.ca/payment-link/6a3a16377a7ef176dee58584', // → Growth
  growth: 'https://links.aibizconnect.ca/payment-link/6a3a165b7a7ef176dee58586',  // → Agency
};

// Compact number format for big meters (12500 → "12.5k", 3000000 → "3M").
function sgCompact(n) {
  if (n >= 1e6) return (n / 1e6).toFixed(n % 1e6 === 0 ? 0 : 1).replace(/\.0$/, '') + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(n % 1e3 === 0 || n >= 1e4 ? 0 : 1).replace(/\.0$/, '') + 'k';
  return String(n);
}
// Small labeled usage meter (used / max) for the plan card.
function SGMeter({ label, used, max, compact, title }) {
  const hasMax = typeof max === 'number' && max > 0;
  const u = typeof used === 'number' ? used : 0;
  const pct = hasMax ? Math.min(100, Math.round((u / max) * 100)) : 0;
  const near = hasMax && pct >= 90;
  const fmt = (n) => (compact ? sgCompact(n) : n.toLocaleString());
  return (
    <div style={{ marginBottom: 9 }} title={title}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11.5, color: 'rgba(226,232,240,0.72)' }}>{label}</span>
        <span className="sg-tnum" style={{ fontSize: 11, fontWeight: 600, color: near ? '#FCA5A5' : 'rgba(226,232,240,0.72)' }}>{fmt(u)} / {hasMax ? fmt(max) : '—'}</span>
      </div>
      <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', borderRadius: 999, background: near ? 'linear-gradient(90deg,#F59E0B,#EF4444)' : 'var(--sg-grad-violet)' }} />
      </div>
    </div>
  );
}

function SGSidebar({ active, onNavigate, collapsed, admin }) {
  const brand = window.SGLive.useBranding();
  // Real plan + usage for the meter card (used vs. max, from /api/account + approved set).
  const [acc, setAcc] = React.useState(null);
  // Every meter now comes from ONE response, so the used figure and the cap are
  // always counted on the same basis. The tracked-rankings number used to come
  // from a second, DOMAIN-scoped call while the cap was account-wide, so it
  // reported one site's keywords against the whole account's allowance.
  // Re-read on site change and after approvals, or it goes stale.
  const { site } = window.SGLive.useLive();
  const bump = window.SGLive.useUsageVersion ? window.SGLive.useUsageVersion() : 0;
  React.useEffect(() => {
    let alive = true;
    window.SGLive.fetchAccount().then((a) => { if (alive) setAcc(a); }).catch(() => {});
    return () => { alive = false; };
  }, [site, bump]);
  const rankUsed = acc ? acc.rankingsUsed : null;
  const rankBreakdown = (acc && Array.isArray(acc.rankingsByDomain) ? acc.rankingsByDomain : [])
    .filter((r) => r.n > 0)
    .map((r) => (r.domain || 'unassigned') + ': ' + r.n)
    .join(String.fromCharCode(10));
  const planName = acc && acc.plan ? acc.plan.charAt(0).toUpperCase() + acc.plan.slice(1) : '';
  // Who's actually signed in. This was hard-coded to one person's name, so every
  // other client saw someone else's identity in their own sidebar.
  const displayName = (acc && (acc.accountName || acc.accountEmail)) || 'Your account';
  const roleLine = (acc && acc.admin) ? 'Agency Admin' : (planName ? planName + ' plan' : 'Client');
  const initials = displayName.replace(/[^A-Za-z ]/g, ' ').trim().split(/\s+/).slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase()).join('') || '?';
  const upgradeUrl = acc ? SG_UPGRADE_LINK[acc.plan] : null;
  const showUpgrade = !!upgradeUrl && !(acc && acc.whitelabel);
  const groups = admin
    ? SG_NAV.concat([{ group: 'Agency', items: [{ id: 'admin', label: 'Client Management', icon: 'users' }] }])
    : SG_NAV;
  return (
    <aside style={{
      width: collapsed ? 0 : 248, flex: 'none', background: 'var(--sg-grad-navy)',
      height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'width 200ms var(--sg-ease)', position: 'relative',
    }}>
      {/* soft violet glow behind the logo, hints at the AI brand */}
      <div style={{ position: 'absolute', top: -40, left: -30, width: 180, height: 180, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.30), transparent 70%)', pointerEvents: 'none' }} />

      {/* Logo */}
      <div style={{ padding: '20px 18px 16px', display: 'flex', alignItems: 'center', gap: 11, position: 'relative' }}>
        <span style={{ width: 34, height: 34, borderRadius: 10, background: brand.whitelabel ? brand.accent : 'var(--sg-grad-brand)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(37,99,235,0.45)' }}>
          {brand.logo
            ? <img src={brand.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <Icon name="play" size={15} color="#fff" />}
        </span>
        <div style={{ lineHeight: 1.05 }}>
          <div style={{ fontFamily: 'var(--sg-font-logo)', fontWeight: 600, fontSize: 16, color: '#fff', letterSpacing: '-0.01em' }}>
            {brand.whitelabel
              ? brand.name
              : <React.Fragment>ABC <span style={{ color: 'var(--sg-violet-500)' }}>SEO/GEO</span></React.Fragment>}
          </div>
          <div style={{ fontSize: 10.5, fontWeight: 500, letterSpacing: '0.14em', color: 'rgba(184,204,247,0.65)', textTransform: 'uppercase', marginTop: 2 }}>SEO + GEO Suite</div>
        </div>
      </div>

      {/* Nav groups */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 12px', overflowY: 'auto', flex: 1 }}>
        {groups.map((g) => (
          <div key={g.group} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'rgba(148,163,184,0.7)', padding: '4px 12px 4px' }}>{g.group}</div>
            {g.items.map((it) => (
              <SGNavItem key={it.id} item={it} active={active === it.id} onClick={() => onNavigate(it.id)} />
            ))}
          </div>
        ))}
      </nav>

      {/* PRO usage card */}
      <div style={{ padding: 14, position: 'relative' }}>
        <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 'var(--sg-radius-lg)', padding: 16, color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
            <Icon name="zap" size={15} color="var(--sg-violet-500)" />
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{planName ? planName + ' plan' : 'Your plan'}</span>
            <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
              color: '#C9B6F7', background: 'rgba(139,92,246,0.22)', padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)' }}>USAGE</span>
          </div>
          {/* This card sits under the site picker, so it reads as belonging to
              the selected site. Every allowance here is shared across the whole
              account, and none of these numbers change when you switch site —
              say so, rather than letting it look stuck. */}
          <div style={{ fontSize: 10.5, color: 'rgba(226,232,240,0.55)', marginTop: -6, marginBottom: 10, lineHeight: 1.4 }}>
            Plan totals — shared across all your sites{acc && acc.used ? ' (' + acc.used + ')' : ''}, not just the one selected.
          </div>
          {/* Two keyword meters sit side by side and they measure different
              things. This one counts paid LOOKUPS in the current 30-day period;
              "Tracked rankings" below counts the keywords you actually track.
              Both showing "/250" made 8/250 read as "I only have 8 keywords"
              while 54 were tracked, so the label has to carry the distinction. */}
          <SGMeter label="Research lookups (30d)" used={acc ? acc.keywordsUsed : null} max={acc ? acc.keywordLimit : null}
            title={'Paid keyword lookups used this 30-day period (volume, difficulty, "find similar"). Resets monthly. This is NOT how many keywords you track — see Tracked rankings below.'} />
          <SGMeter label="AI tokens" compact used={acc ? acc.aiUsed : null} max={acc ? acc.aiLimit : null} />
          <SGMeter label="Tracked rankings" used={rankUsed} max={acc ? acc.rankingLimit : null}
            title={'Keywords you have approved and we check positions for, across all your sites. ' + (rankBreakdown ? '(' + rankBreakdown + ')' : '')} />
          <SGMeter label="Websites" used={acc ? acc.used : null} max={acc ? acc.domainLimit : null} />
          {showUpgrade ? (
            <button onClick={() => { try { window.open(upgradeUrl, '_blank', 'noopener'); } catch (e) { /* noop */ } }}
              style={{ marginTop: 5, width: '100%', height: 36, border: 'none', borderRadius: 'var(--sg-radius-pill)',
              background: '#fff', color: 'var(--sg-navy-900)', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Upgrade plan
            </button>
          ) : (acc && acc.plan === 'agency' && !acc.whitelabel ? (
            <div style={{ marginTop: 5, textAlign: 'center', fontSize: 11, color: 'rgba(226,232,240,0.55)' }}>You're on the top plan</div>
          ) : null)}
        </div>

        {/* User row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '8px 8px', borderRadius: 'var(--sg-radius-md)', cursor: 'pointer' }}>
          <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--sg-grad-violet)', flex: 'none',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>{initials}</span>
          <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
            <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleLine}</div>
          </div>
          <button onClick={() => window.SGLive.logout()} title="Sign out"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(148,163,184,0.85)', fontFamily: 'var(--sg-font-sans)', fontWeight: 600, fontSize: 12, padding: 4 }}>
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}

window.SGSidebar = SGSidebar;
